/**
 * Cliente HTTP da API.
 *
 * `credentials: 'include'` é obrigatório: a sessão trafega em cookie `HttpOnly`,
 * fora do alcance de script na página. Sem essa opção o login parece funcionar e a
 * sessão não se mantém — o defeito mais confuso de depurar nesta camada.
 */

export interface ErroDaApi {
  readonly mensagem: string;
  readonly codigo: string;
  readonly campo?: string;
  readonly camposInvalidos?: Record<string, string[]>;
}

export class FalhaDaApi extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly campo?: string;
  readonly camposInvalidos?: Record<string, string[]>;

  constructor(status: number, corpo: ErroDaApi) {
    super(corpo.mensagem);
    this.name = 'FalhaDaApi';
    this.status = status;
    this.codigo = corpo.codigo;
    if (corpo.campo) this.campo = corpo.campo;
    if (corpo.camposInvalidos) this.camposInvalidos = corpo.camposInvalidos;
  }

  /** Sessão expirada — a interface reage levando de volta ao login. */
  get exigeLogin(): boolean {
    return this.status === 401;
  }
}

type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface Opcoes {
  readonly metodo?: Metodo;
  readonly corpo?: unknown;
  readonly params?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Renovação da sessão do escritório.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUE ISTO EXISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O token de acesso dura 30 minutos e a rota de renovação sempre existiu — o
 * `.env` até diz "curta de propósito, a renovação é automática". Só que ninguém a
 * chamava: a tela de abertura renovava UMA VEZ, ao carregar a página, e daí em
 * diante toda requisição passava a devolver 401.
 *
 * O efeito era pior do que cair: a tela aceitava o preenchimento e nada gravava.
 * Meia hora de trabalho e alguém jura ter cadastrado um devedor que nunca existiu.
 *
 * `emCurso` faz o pedido ser UM só: uma tela com seis consultas em paralelo
 * expirando junto renovaria seis vezes, e as concorrentes invalidariam umas às
 * outras — o que derruba a sessão em vez de salvá-la.
 */
let emCurso: Promise<boolean> | null = null;

async function renovarSessao(): Promise<boolean> {
  emCurso ??= (async () => {
    try {
      const r = await fetch(new URL('/api/autenticacao/renovar', window.location.origin), {
        method: 'POST',
        credentials: 'include',
      });
      return r.ok;
    } catch {
      return false;
    }
  })();

  const ok = await emCurso;
  emCurso = null;
  return ok;
}

/**
 * Avisado quando a sessão morre de verdade — renovação tentada e recusada.
 *
 * Sem isto, o 401 definitivo vira erro solto no meio da tela e a pessoa continua
 * digitando. A sessão precisa APARECER encerrada, com o login de volta.
 */
type AoPerderSessao = () => void;
let aoPerder: AoPerderSessao | null = null;

export function registrarPerdaDeSessao(cb: AoPerderSessao | null): void {
  aoPerder = cb;
}

/** Rotas que não podem tentar renovar: o portal tem sessão própria, e as de
 *  autenticação renovando a si mesmas entrariam em laço. */
function podeRenovar(caminho: string): boolean {
  return !caminho.startsWith('/portal') && !caminho.startsWith('/autenticacao');
}

async function requisitar<T>(caminho: string, opcoes: Opcoes = {}, jaRenovou = false): Promise<T> {
  const url = new URL(`/api${caminho}`, window.location.origin);

  for (const [chave, valor] of Object.entries(opcoes.params ?? {})) {
    if (valor !== undefined && valor !== null && valor !== '') {
      url.searchParams.set(chave, String(valor));
    }
  }

  const inicio: RequestInit = {
    method: opcoes.metodo ?? 'GET',
    credentials: 'include',
  };
  // Cabeçalho e corpo só entram quando há corpo — com `exactOptionalPropertyTypes`,
  // atribuir `undefined` não é o mesmo que omitir a propriedade.
  if (opcoes.corpo !== undefined) {
    inicio.headers = { 'Content-Type': 'application/json' };
    inicio.body = JSON.stringify(opcoes.corpo);
  }

  const resposta = await fetch(url, inicio);

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (resposta.status === 401 && !jaRenovou && podeRenovar(caminho)) {
    /* Uma tentativa, e só uma: `jaRenovou` impede que um 401 legítimo depois da
       renovação bem-sucedida entre em laço. */
    if (await renovarSessao()) return requisitar<T>(caminho, opcoes, true);
    aoPerder?.();
  }

  if (!resposta.ok) {
    const corpo = (dados ?? {}) as ErroDaApi;
    throw new FalhaDaApi(resposta.status, {
      mensagem: corpo.mensagem ?? 'Não foi possível concluir a operação.',
      codigo: corpo.codigo ?? 'erro',
      ...(corpo.campo ? { campo: corpo.campo } : {}),
      ...(corpo.camposInvalidos ? { camposInvalidos: corpo.camposInvalidos } : {}),
    });
  }

  return dados as T;
}

export const api = {
  buscar: <T>(caminho: string, params?: Opcoes['params']) =>
    requisitar<T>(caminho, params ? { params } : {}),
  criar: <T>(caminho: string, corpo?: unknown) =>
    requisitar<T>(caminho, { metodo: 'POST', corpo }),
  substituir: <T>(caminho: string, corpo?: unknown) =>
    requisitar<T>(caminho, { metodo: 'PUT', corpo }),
  alterar: <T>(caminho: string, corpo?: unknown) =>
    requisitar<T>(caminho, { metodo: 'PATCH', corpo }),
  remover: <T>(caminho: string) => requisitar<T>(caminho, { metodo: 'DELETE' }),
};
